import React, { useState, useEffect, useRef, useCallback, memo } from 'react';

/**
 * LazyTemplateCard - Only renders the iframe when the card scrolls into view.
 * When scrolled out of view, the iframe is unmounted to free memory.
 * Uses fixed A4 dimensions (794×1123px) and dynamically scales to fit the card.
 *
 * Props:
 * - iframeSrc:    URL to a pre-rendered static preview (preferred — uses <iframe src>).
 *                  When provided, takes priority over renderedHTML for guaranteed parity
 *                  between localhost and production hosting.
 * - renderedHTML: The rendered template HTML string (legacy path — uses <iframe srcDoc>).
 *                  Used only when iframeSrc is not available.
 * - templateName: Template name for alt/title.
 * - rootMargin:   IntersectionObserver margin (default: '200px').
 */
const LazyTemplateCard = memo(({ iframeSrc, renderedHTML, templateName, rootMargin = '200px' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [scale, setScale] = useState(0);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const resizeObserverRef = useRef(null);

  // Calculate scale based on container width using ResizeObserver for reliability
  const calcScale = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      if (containerWidth > 0) {
        // A4 width = 794px; scale to fit container
        const newScale = Math.min(containerWidth / 794, 1);
        setScale(newScale);
      }
    }
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Use ResizeObserver for accurate width detection (handles initial layout)
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(() => {
        calcScale();
      });
      resizeObserverRef.current.observe(element);
    } else {
      // Fallback: calculate after paint
      requestAnimationFrame(() => {
        requestAnimationFrame(calcScale);
      });
      window.addEventListener('resize', calcScale);
    }

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      window.removeEventListener('resize', calcScale);
    };
  }, [calcScale]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsVisible(visible);
        if (visible) {
          setHasBeenVisible(true);
        }
      },
      {
        rootMargin,
        threshold: 0
      }
    );

    observerRef.current.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [rootMargin]);

  // Calculate the visible height based on scale to set container aspect ratio
  const scaledHeight = scale > 0 ? Math.round(1123 * scale) : 0;

  // Prefer the static pre-rendered URL when available (same-origin <iframe src>)
  // Falls back to runtime srcDoc rendering only when no iframeSrc is provided
  const hasContent = !!(iframeSrc || renderedHTML);

  return (
    <div
      ref={containerRef}
      className="lazy-template-container"
      style={scaledHeight > 0 ? { height: `${scaledHeight}px` } : undefined}
    >
      {isVisible && hasContent && scale > 0 ? (
        iframeSrc ? (
          <iframe
            src={iframeSrc}
            title={templateName}
            className="template-preview"
            scrolling="no"
            loading="eager"
            style={{ transform: `scale(${scale})` }}
          />
        ) : (
          <iframe
            srcDoc={renderedHTML}
            title={templateName}
            className="template-preview"
            scrolling="no"
            loading="eager"
            style={{ transform: `scale(${scale})` }}
          />
        )
      ) : hasBeenVisible && hasContent ? (
        // Was visible before, show a static placeholder to avoid re-render cost
        <div className="template-preview-placeholder">
          <div className="placeholder-text">Scroll to view</div>
        </div>
      ) : !hasContent ? (
        <div className="template-preview-loading">
          <div className="preview-loading-spinner"></div>
        </div>
      ) : null}
    </div>
  );
});

LazyTemplateCard.displayName = 'LazyTemplateCard';

export default LazyTemplateCard;
